import ruLocale from "date-fns/locale/ru";
import get from "lodash/get";
import React, { FC } from "react";
import { Button } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";

import DealerChart from "~/components/Chart";
import { MANAGING_Q_INDICATORS_PERIOD_OPTIONS } from "~/constans/quartiles";
import { DATA_STATUS_TYPES } from "~/constans/statuses";
import { useGetLocationData } from "~/hooks/locationData";
import { getIndicatorValue } from "~/libs/getData";
import { getIndicatorPeriod } from "~/libs/getFormatDate";
import { newFormat, showDate } from "~/pages/Q/formater";
import {
  useGetQDataByLineQuery,
  useGetQOneRatingReportsLazyQuery,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { useCurrentDealerId } from "~/shared/hooks/useCurrentDealerId";
import { useNotify } from "~/shared/hooks/useNotify";
import { TableV2 } from "~/shared/ui/TableV2";

import s from "./QWithChartView.less";

interface IProps {
  currentDate?;
}

const QuartileWithChartView: FC<IProps> = ({ currentDate }) => {
  const { t } = useTranslation();
  const lang = localStorage.getItem("language");
  const currentDealerId = useCurrentDealerId();
  const { hasPermission, loading: loadingPermissions } = usePermissions();
  const noCaching = sessionStorage.getItem("noCaching");
  const { currentId } = useGetLocationData();
  const { notifyReport } = useNotify();
  const [getQOneRatingReportsLazyQuery, QOneRatingReportsLazyQuery] =
    useGetQOneRatingReportsLazyQuery({
      onCompleted(data) {
        const hash = get(data, "QuartilesRatingGetter.getReports.fileHash");
        hash && notifyReport(hash);
      },
      fetchPolicy: "no-cache",
    });

  const { data, loading } = useGetQDataByLineQuery({
    variables: {
      DealerId: currentDealerId,
      QuartilesRatingId: currentId,
      date: newFormat(currentDate),
    },
    fetchPolicy: noCaching ? "no-cache" : "cache-first",
  });

  const {
    LastPeriodIndicator = {},
    ValueIndicator = {},
    GroupIndicator = {},
    AverageIndicator = {},
    DifferenceIndicator = {},
    GroupAverageIndicator = {},
    round,
    format,
    KPIru,
    KPIen,
  } = get(data, "QuartilesRatingGetter.getRatingDataByLine", {});
  const lastPeriod = get(LastPeriodIndicator, "period", "");
  const LastPeriodIndicatorValues = get(LastPeriodIndicator, "Values", [])
    .map((indicator) => getIndicatorPeriod(indicator, lastPeriod))
    .filter((ind, index, arr) => arr.indexOf(ind) === index);

  const qs = LastPeriodIndicatorValues.map((period) => {
    const getCurrentValue = (indicator, string = false) => {
      const newRound = string ? null : round;
      const values = get(indicator, "Values", []).filter(
        (value) => getIndicatorPeriod(value, lastPeriod) === period,
      );
      const indValue =
        values.length > 0 &&
        values[values.length - 1][string ? "stringValue" : "doubleValue"];
      return getIndicatorValue(indicator, newRound, format, indValue);
    };

    return {
      LastPeriodIndicatorId: period,
      ValueIndicatorId: getCurrentValue(ValueIndicator),
      GroupIndicatorId: getCurrentValue(GroupIndicator, true),
      AverageIndicatorId: getCurrentValue(AverageIndicator),
      DifferenceIndicatorId: getCurrentValue(DifferenceIndicator),
      GroupAverageIndicatorId: getCurrentValue(GroupAverageIndicator),
    };
  });

  const severalChartsQs =
    qs.length > 0 &&
    qs.reduce(
      (acc, q) => {
        for (const key in acc) {
          !isNaN(q[key])
            ? acc[key].push(q[key])
            : q[key] === DATA_STATUS_TYPES.DASH && acc[key].push(0);
        }
        return acc;
      },
      {
        ValueIndicatorId: [],
        AverageIndicatorId: [],
        DifferenceIndicatorId: [],
        GroupAverageIndicatorId: [],
      },
    );

  const getQOneRatingReports = () => {
    getQOneRatingReportsLazyQuery({
      variables: {
        DealerId: currentDealerId,
        QuartilesRatingId: currentId,
        date: currentDate,
        lang,
      },
    });
  };

  return (
    <div className={s.q_view_wrapper}>
      <header className={s.s_header}>
        <label className={s.s_title}>
          {`${lang === "en" ? KPIen : KPIru} ${t("ui.from&")} ${showDate(
            currentDate,
          )}`}
        </label>
        {hasPermission("quartilesRatings.reports.get") && (
          <Button
            size={"middle"}
            color={"success"}
            onClick={getQOneRatingReports}
            loading={QOneRatingReportsLazyQuery.loading}
          >
            {t("q.save_excel")}
          </Button>
        )}
      </header>
      <DealerChart
        labels={LastPeriodIndicatorValues}
        type="quartiles"
        severalChartsValues={severalChartsQs}
        loading={loading}
        customClassName={s.chart}
      />
      <div className={s.scroll_wrap}>
        <TableV2
          dataSource={[...qs].reverse()}
          options={MANAGING_Q_INDICATORS_PERIOD_OPTIONS}
          loading={loading}
          type="q"
          wrapperClassName={s.current_q_view_table}
        />
      </div>
    </div>
  );
};
export default QuartileWithChartView;
